import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/api";
import { Video, VideoOff, Radio, ShieldAlert, Loader2, VolumeX } from "lucide-react";

const MIME_CANDS = [
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
  'video/mp4;codecs="avc1.64001f,mp4a.40.2"',
  "video/mp4;codecs=h264,aac",
  "video/mp4",
  "video/webm",
];

function pickMime() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  for (const m of MIME_CANDS) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (_) {}
  }
  return null;
}

// Advertise a full codec mime to the viewer (iOS often reports bare "video/mp4")
function fullMime(m) {
  if (!m) return 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"';
  if (m.includes("codecs")) return m;
  if (m.startsWith("video/mp4")) return 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"';
  if (m.startsWith("video/webm")) return "video/webm;codecs=vp8,opus";
  return m;
}

export default function LivePhone() {
  const { token } = useParams();
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const jpegTimerRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | connecting | live | ended | error
  const [err, setErr] = useState("");
  const [noAudio, setNoAudio] = useState(false);
  const [supported] = useState(() => typeof window !== "undefined" && !!navigator.mediaDevices && !!navigator.mediaDevices.getUserMedia);

  useEffect(() => {
    api.get(`/live/${token}`).then(({ data }) => setInfo(data)).catch(() => setErr("Deze link is niet (meer) geldig."));
    return () => stop(); // eslint-disable-line
  }, [token]);

  const stop = () => {
    try { jpegTimerRef.current && clearInterval(jpegTimerRef.current); } catch (_) {}
    try { recRef.current && recRef.current.state !== "inactive" && recRef.current.stop(); } catch (_) {}
    try { wsRef.current && wsRef.current.close(); } catch (_) {}
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) {}
    jpegTimerRef.current = null; recRef.current = null; wsRef.current = null; streamRef.current = null;
  };

  const startMedia = (stream, ws, mime) => {
    let rec;
    try { rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 1200000 }); }
    catch (e) { startJpeg(stream, ws); return; }
    recRef.current = rec;
    const adv = fullMime(rec.mimeType || mime);
    try { ws.send(JSON.stringify({ type: "mode", mode: "media" })); ws.send(JSON.stringify({ type: "mime", mime: adv })); } catch (_) {}
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0 && ws.readyState === 1) e.data.arrayBuffer().then((b) => { try { ws.send(b); } catch (_) {} });
    };
    rec.onerror = () => { try { rec.stop(); } catch (_) {} startJpeg(stream, ws); };
    try { rec.start(700); } catch (e) { startJpeg(stream, ws); return; }
    setStatus("live");
  };

  const startJpeg = (stream, ws) => {
    setNoAudio(true);
    try { ws.send(JSON.stringify({ type: "mode", mode: "jpeg" })); } catch (_) {}
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    jpegTimerRef.current = setInterval(() => {
      const v = videoRef.current;
      if (!v || !v.videoWidth || ws.readyState !== 1) return;
      const w = Math.min(960, v.videoWidth);
      const h = Math.round(w * (v.videoHeight / v.videoWidth));
      canvas.width = w; canvas.height = h;
      ctx.drawImage(v, 0, 0, w, h);
      canvas.toBlob((blob) => { if (blob && ws.readyState === 1) blob.arrayBuffer().then((b) => { try { ws.send(b); } catch (_) {} }); }, "image/jpeg", 0.5);
    }, 150);
    setStatus("live");
  };

  const start = async () => {
    setErr(""); setStatus("connecting");
    const mime = pickMime();
    const useMedia = !!(window.MediaRecorder && mime);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: useMedia ? (info?.audio !== false) : false,
      });
    } catch (e) {
      setErr("Geen toegang tot camera/microfoon. Sta toegang toe in je browser en probeer opnieuw.");
      setStatus("error");
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
    const wsBase = BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/live/${token}?role=broadcaster`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onopen = () => { if (useMedia) startMedia(stream, ws, mime); else startJpeg(stream, ws); };
    ws.onclose = () => setStatus((s) => (s === "live" || s === "connecting" ? "ended" : s));
    ws.onerror = () => setErr("Verbinding met de meldkamer mislukt.");
  };

  const invalid = err.includes("geldig");

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="live-phone-page">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-5 py-6">
        <div className="flex items-center gap-2 text-sm font-semibold tracking-wide text-blue-300">
          <Radio className="h-4 w-4" /> DispatchMe · Live meekijken
        </div>

        {err && (
          <div data-testid="live-phone-error" className="mt-4 flex items-start gap-2 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {err}
          </div>
        )}

        {info && (
          <div className="mt-4">
            <h1 className="font-display text-xl font-bold">De meldkamer wil live meekijken</h1>
            {info.incident_title && <p className="mt-1 text-sm text-slate-300">Melding: <span className="font-semibold text-white">{info.incident_title}</span></p>}
            <p className="mt-2 text-sm text-slate-400">Richt je camera op de situatie. Beeld{noAudio ? "" : " en geluid"} wordt alleen met de meldkamer gedeeld.</p>
          </div>
        )}

        <div className="mt-5 flex-1">
          <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl border border-slate-800 bg-black">
            <video ref={videoRef} data-testid="live-phone-video" playsInline muted className="h-full w-full object-cover" />
            {status === "live" && (
              <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold">
                <span className="h-2 w-2 animate-pulse rounded-full bg-white" /> LIVE
              </div>
            )}
            {status !== "live" && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500"><Video className="h-10 w-10" /></div>
            )}
          </div>
          {noAudio && status === "live" && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300"><VolumeX className="h-3.5 w-3.5" /> Op dit toestel wordt alleen beeld gedeeld (geen geluid).</p>
          )}
        </div>

        <div className="mt-5 space-y-3">
          {!supported && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
              Deze browser ondersteunt camera-delen niet.
            </div>
          )}
          {(status === "idle" || status === "error" || status === "ended") && supported && !invalid && (
            <button data-testid="live-phone-start" onClick={start} disabled={!info}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-4 text-base font-bold hover:bg-blue-500 disabled:opacity-50">
              <Video className="h-5 w-5" /> {status === "ended" || status === "error" ? "Opnieuw delen" : "Camera delen"}
            </button>
          )}
          {status === "connecting" && (
            <div className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-800 py-4 text-base font-semibold">
              <Loader2 className="h-5 w-5 animate-spin" /> Verbinden…
            </div>
          )}
          {status === "live" && (
            <button data-testid="live-phone-stop" onClick={() => { stop(); setStatus("ended"); }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 py-4 text-base font-bold hover:bg-red-500">
              <VideoOff className="h-5 w-5" /> Stoppen met delen
            </button>
          )}
          {status === "ended" && <p className="text-center text-sm text-slate-400">Delen gestopt.</p>}
        </div>
      </div>
    </div>
  );
}
